/* eslint-disable no-negated-condition */
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useGlobalContext } from '@/context/global.context';
import useApiData from '@/hooks/useApiData';
import { getEmisor, putEmisor } from '@/services/emisor.service';
import { getMunicipiosByProvincia, getProvincias, getTipoVia, getMunicipiosByCp } from '@/services/maestros.service';

import { dropdownPriorityOptions } from '@/constants/empresa.constants';
import { notificationsConfig } from '@/helpers/notifications';
import { type IEmpresaContext, type IEmpresaForm } from '@/interfaces/context/IEmpresa.context.interface';
import { type Priordades, type IEmisor, type IListaTipoNotificacion } from '@/interfaces/model/emisor.interface';

import { useDirtyForm } from './dirtyForm.context';

type EmpresaProps = {
	readonly children: React.ReactNode;
};

const EmpresaContext = createContext<IEmpresaContext | undefined>(undefined);

export const EmpresaProvider: React.FC<EmpresaProps> = ({ children }: EmpresaProps) => {
	const {
		control,
		watch,
		setValue,
		getValues,
		formState: { isDirty, touchedFields, dirtyFields },
	} = useForm<IEmpresaForm>({
		defaultValues: {
			isLoading: false,
			isEdit: false,
			emisor: {},
			defaultEmisor: {},
		},
	});

	const mensajeErrorCp = 'Código postal erróneo';
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { setIsDirtyForm } = useDirtyForm();

	const isEdit = watch('isEdit');
	const emisor = watch('emisor');
	const codigoPostalSeleccionado = watch('emisor.direccion.codigoPostal');
	const provinciaSeleccionada = watch('emisor.direccion.codigoProvincia');
	const municipioSeleccionado = watch('emisor.direccion.codigoMunicipio');
	const municipios = watch('municipios');
	const email = watch('emisor.email');
	const telefono = watch('emisor.telefono');

	const [errorEmail, setErrorEmail] = useState<string | undefined>(undefined);
	const { user } = useGlobalContext();
	const { loading, triggerFetch } = useApiData({ loading: ['getData' as const] });

	const checkField = useCallback(
		(path: string): string | undefined => {
			const isTouched = path.split('.').reduce((obj, key) => (obj as Record<string, any>)?.[key], touchedFields);
			if (!isTouched) return undefined;
			const value = getValues(path as any);
			return value ? undefined : 'Campo requerido';
		},
		[getValues, touchedFields],
	);

	useEffect(() => {
		if (!email) {
			setErrorEmail(undefined);
			return;
		}
		const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
		setErrorEmail(emailRegex.test(email) ? undefined : 'Formato inválido');
	}, [email]);

	// Toca hacer todo esto para el modal de cambios sin guardar.
	useEffect(() => {		
		const emisorChanges = dirtyFields.emisor ? Object.keys(dirtyFields.emisor) : [];		
		const ignoreFields = ['codigoProvincia', 'codigoMunicipio', 'descripcionMunicipio'];
		const hasMainFieldsChanges = emisorChanges.some((key) => key !== 'direccion');		
		const dirtyDireccion = dirtyFields.emisor?.direccion ?? {};
		const direccionChanges = Object.keys(dirtyDireccion);
		const hasRealDireccionChanges = direccionChanges.some((key) => !ignoreFields.includes(key));
		
		if (hasMainFieldsChanges || hasRealDireccionChanges) {
			setIsDirtyForm(isDirty);
		} else {			
			setIsDirtyForm(false);
		}		
	}, [JSON.stringify(dirtyFields), isDirty, setIsDirtyForm]);

	const loadMunicipios = useCallback(
		async (provincia: string) => {
			try {
				const { data } = await getMunicipiosByProvincia(provincia);
				setValue('municipios', data, { shouldDirty: false });
				return data;
			} catch (error) {
				console.error('Error cargando municipios:', error);
				return [];
			}
		},
		[setValue],
	);

	const getData = useCallback(async () => {
		if (!user) return;

		const [resEmisor, resProvincias, resVias] = await Promise.all([
			triggerFetch({
				payload: { nifEmisor: user.nifEmisor, idTipoSector: user.tipoSectorEmisor.id },
				fetch: getEmisor,
				name: 'getData',
				error: 'Error al cargar el emisor',
			}),
			getProvincias(),
			getTipoVia(),
		]);

		if (resEmisor.data) {
			const emisorData = { ...resEmisor.data, direccion: resEmisor.data.direccion ?? {} };
			setValue('emisor', emisorData, { shouldDirty: false });
			setValue('defaultEmisor', emisorData, { shouldDirty: false });

			if (emisorData.direccion?.codigoProvincia) {
				await loadMunicipios(emisorData.direccion.codigoProvincia);
			}
		}
		setValue('provincias', resProvincias.data, { shouldDirty: false });
		setValue('tiposDeVia', resVias.data, { shouldDirty: false });
	}, [user, triggerFetch, setValue, loadMunicipios]);

	useEffect(() => {
		getData();
	}, []);

	useEffect(() => {
		const fetchCpData = async () => {
			const cp = codigoPostalSeleccionado?.toString();
			if (cp?.length !== 5) return;

			try {
				const { data: dataCp } = await getMunicipiosByCp(cp);
				if (dataCp?.municipio?.[0]) {
					const info = dataCp.municipio[0];
					setValue('emisor.direccion.codigoProvincia', info.codigoProvincia, { shouldDirty: true });

					const listaMunicipios = await loadMunicipios(info.codigoProvincia);
					setValue('emisor.direccion.codigoMunicipio', info.codigo, { shouldDirty: true });

					const municipioEncontrado = listaMunicipios.find((m: any) => m.codigo === info.codigo);
					if (municipioEncontrado) {
						setValue('emisor.direccion.descripcionMunicipio', municipioEncontrado.nombre, {
							shouldDirty: true,
						});
					}

					setValue('errorCP', undefined);
				} else {
					setValue('errorCP', mensajeErrorCp);
				}
			} catch (error) {
				console.error('Error al obtener CP:', error);
			}
		};
		fetchCpData();
	}, [codigoPostalSeleccionado, setValue, loadMunicipios]);

	useEffect(() => {
		if (provinciaSeleccionada) {
			void loadMunicipios(provinciaSeleccionada);
			const prov = getValues('provincias')?.find((it) => it.codigo === provinciaSeleccionada);
			if (prov) setValue('emisor.direccion.descripcionProvincia', prov.nombre);
		} else {
			setValue('municipios', [], { shouldDirty: false });
		}
	}, [provinciaSeleccionada, loadMunicipios, getValues, setValue]);

	useEffect(() => {
		if (municipioSeleccionado && municipios) {
			const muni = municipios.find((it: any) => it.codigo === municipioSeleccionado);
			if (muni) setValue('emisor.direccion.descripcionMunicipio', muni.nombre);
		}
	}, [municipioSeleccionado, municipios, setValue]);

	useEffect(() => {
		if (telefono && telefono.toString().length > 19) {
			setValue('emisor.telefono', telefono.toString().slice(0, 19), { shouldValidate: true });
		}
	}, [telefono, setValue]);

	const transformPrioridadItem = (item: IListaTipoNotificacion) => {
		const option = dropdownPriorityOptions.find((opt) => opt.value === item.prioridad.id);
    
		return {
			...item,
			prioridad: {
				...item.prioridad,
				descripcion: (option?.label ?? item.prioridad.descripcion) as Priordades,
			},
		};
	};

	const mapIEmisor = (): IEmisor | undefined => {
		const emisorActual = getValues('emisor');
		if (!emisorActual?.prioridadesNotificacion) return emisorActual;

		emisor?.prioridadesNotificacion.map((prioridadGrupo) => ({
			...prioridadGrupo,
			listaTipoNotificacionPrioridad: prioridadGrupo.listaTipoNotificacionPrioridad.map((item) => transformPrioridadItem(item))
		}));

		return {
			...emisorActual,
			nombre: undefined,
			fechaAlta: undefined,
			fechaBaja: undefined,
			id: undefined,
			listaTiposTramite: undefined,
			prioridadesNotificacion: emisorActual.prioridadesNotificacion.map((grupo) => ({
				...grupo,
				listaTipoNotificacionPrioridad: grupo.listaTipoNotificacionPrioridad.map((item) => 
					transformPrioridadItem(item)
				),
			})),
		};
	};

	const sendEmisor = useCallback(async () => {
		const emisorForm = getValues('emisor');
		if (!emisorForm) return;

		try {
			setValue('isLoading', true);
			const mapped = mapIEmisor();
			const result = await putEmisor(mapped ?? emisorForm);
			setIsDirtyForm(false);

			if (![200, 201].includes(result.code)) {
				openNotification(notificationTemplate.errorMessage('Error inesperado...'));
			} else {
				await getData();
				setValue('isEdit', false);
			}
		} catch (e) {
			openNotification(notificationTemplate.errorMessage('Error inesperado, inténtelo de nuevo más tarde.'));
		} finally {
			setValue('isLoading', false);
		}
	}, [getValues, setValue, openNotification, notificationTemplate, getData]);

	const setIsEdit = useCallback(
		(value: boolean) => {
			if (!value) setValue('emisor', getValues('defaultEmisor'));
			setValue('isEdit', value);
		},
		[setValue, getValues],
	);

	const contextValue = useMemo(
		(): IEmpresaContext => ({
			...getValues(),
			isEdit,
			emisor,
			errorEmail,
			isLoading: loading.getData,
			control,
			checkField,
			setIsEdit,
			isFormDirty: () => isDirty,
			send: sendEmisor,
			checkCPError() {
				const cp = getValues('emisor')?.direccion?.codigoPostal;
				setValue('errorCP', cp?.toString().length !== 5 ? mensajeErrorCp : undefined);
			},
		}),
		[
			isEdit,
			emisor,
			errorEmail,
			loading.getData,
			control,
			checkField,
			setIsEdit,
			isDirty,
			sendEmisor,
			getValues,
			setValue,
		],
	);

	return <EmpresaContext.Provider value={contextValue}>{children}</EmpresaContext.Provider>;
};

export const useEmpresa = (): IEmpresaContext => {
	const context = useContext(EmpresaContext);
	if (!context) throw new Error('useEmpresa must be used within provider');
	return context;
};
